import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { getPortalSchoolCalendar } from "../../lib/api";
import { DEFAULT_LEGEND, legendLabel } from "../../lib/calendarLegend";

const colorForType = (t) => DEFAULT_LEGEND.find((d) => d.value === t)?.color || "bg-gray-200";
const fmt = (ds) => new Date(ds + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

function monthsBetween(firstDay, lastDay) {
  const start = new Date(firstDay + "T00:00:00");
  const end = new Date(lastDay + "T00:00:00");
  const out = [];
  let y = start.getFullYear();
  let m = start.getMonth();
  while (y < end.getFullYear() || (y === end.getFullYear() && m <= end.getMonth())) {
    out.push({ year: y, month: m });
    m += 1;
    if (m > 11) { m = 0; y += 1; }
  }
  return out;
}

function MonthGrid({ year, month, cal }) {
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startDay = firstDay.getDay();
  const daysInMonth = lastDay.getDate();
  const monthName = firstDay.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  const days = [];
  for (let i = 0; i < startDay; i++) days.push(null);
  for (let d = 1; d <= daysInMonth; d++) days.push(d);

  return (
    <div className="border rounded-lg p-3 bg-white">
      <h3 className="font-bold text-center mb-2 text-[#00305E]">{monthName}</h3>
      <div className="grid grid-cols-7 gap-1 text-xs">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-center font-bold text-gray-500 py-1">{d}</div>
        ))}
        {days.map((day, idx) => {
          if (day === null) return <div key={idx} className="p-1" />;
          const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
          const special = (cal.special_dates || []).find((sd) => sd.date === dateStr);
          const dow = new Date(year, month, day).getDay();
          const isWeekend = dow === 0 || dow === 6;
          const isFirst = dateStr === cal.first_day;
          const isLast = dateStr === cal.last_day;
          let bg = "", title = "";
          if (isFirst) { bg = "bg-green-400"; title = "First Day of School"; }
          else if (isLast) { bg = "bg-green-400"; title = "Last Day of School"; }
          else if (special) { bg = colorForType(special.type); title = special.name; }
          else if (isWeekend) { bg = "bg-gray-100 text-gray-400"; }
          return (
            <div key={idx} className={`p-1 text-center rounded ${bg}`} title={title} data-testid={`portal-cal-day-${dateStr}`}>
              {day}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function PortalCalendar() {
  const [cal, setCal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await getPortalSchoolCalendar();
        const list = Array.isArray(data) ? data : [];
        const today = todayISO();
        // Pick the calendar covering today, else the most recent by first_day.
        const covering = list.find((c) => c.first_day && c.last_day && c.first_day <= today && today <= c.last_day);
        const chosen = covering || [...list].sort((a, b) => (b.first_day || "").localeCompare(a.first_day || ""))[0] || null;
        if (!alive) return;
        setCal(chosen);
      } catch {
        if (alive) setCal(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const keyDates = useMemo(() => {
    if (!cal) return [];
    const rows = [];
    if (cal.first_day) rows.push({ name: "First Day of School", type: "event", start: cal.first_day });
    if (cal.last_day) rows.push({ name: "Last Day of School", type: "event", start: cal.last_day });
    const holidays = (cal.special_dates || []).filter((d) => ["no_school", "vacation", "holiday"].includes(d.type));
    const grouped = [...new Map(holidays.map((d) => [`${d.name}-${d.type}`, d])).values()];
    grouped.forEach((d) => {
      const same = holidays.filter((x) => x.name === d.name && x.type === d.type).map((x) => x.date).sort();
      rows.push({ name: d.name, type: d.type, start: same[0], end: same.length > 1 ? same[same.length - 1] : null });
    });
    return rows.sort((a, b) => (a.start || "").localeCompare(b.start || ""));
  }, [cal]);

  if (loading) {
    return <Card><CardContent className="py-16 text-center text-muted-foreground">Loading calendar…</CardContent></Card>;
  }
  if (!cal) {
    return (
      <Card data-testid="portal-calendar-empty">
        <CardContent className="py-16 text-center text-muted-foreground">
          The school calendar has not been published yet. Please check back later.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="portal-calendar">
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{cal.school_name || "School Year Calendar"}</CardTitle>
          <CardDescription>{cal.academic_year}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {(cal.notice || "").trim() && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 p-4 flex items-start gap-3" data-testid="portal-calendar-notice">
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-amber-600 mt-0.5 shrink-0"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <p className="text-sm text-amber-900 whitespace-pre-wrap">{cal.notice}</p>
            </div>
          )}
          <div className="flex flex-wrap gap-3 p-3 bg-gray-50 rounded-lg border text-xs sm:text-sm">
            <span className="font-medium">Legend:</span>
            {DEFAULT_LEGEND.map((t) => (
              <span key={t.value} className="flex items-center gap-1">
                <span className={`w-3.5 h-3.5 rounded ${t.color}`} />{legendLabel(cal, t.value, t.label)}
              </span>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {monthsBetween(cal.first_day, cal.last_day).map(({ year, month }) => (
              <MonthGrid key={`${year}-${month}`} year={year} month={month} cal={cal} />
            ))}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {keyDates.length > 0 && (
          <Card>
            <CardHeader><CardTitle className="text-base">Key Dates</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {keyDates.map((d, i) => (
                <div key={`${d.name}-${i}`} className="flex items-center justify-between p-2 rounded border bg-slate-50">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-3 h-3 rounded shrink-0 ${colorForType(d.type)}`} />
                    <span className="text-sm truncate">{d.name}</span>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">{d.end ? `${fmt(d.start)} – ${fmt(d.end)}` : fmt(d.start)}</span>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
